import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { NetworkError, resetPassword } from '../services/authService';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const accessToken = searchParams.get('access_token') || hashParams.get('access_token') || '';
  const refreshToken = searchParams.get('refresh_token') || hashParams.get('refresh_token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const canSubmit = useMemo(() => {
    return password.length >= 8 && confirmPassword.length >= 8;
  }, [password, confirmPassword]);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;
    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage(null);
      setSuccessMessage(null);
      await resetPassword({
        newPassword: password,
        accessToken: accessToken || undefined,
        refreshToken: refreshToken || undefined,
      });
      setSuccessMessage('Password updated. You can now sign in.');
      setPassword('');
      setConfirmPassword('');
    } catch (error) {
      if (error instanceof NetworkError) {
        setErrorMessage(error.message);
      } else if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('Unable to reset password.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fbfd] px-6 py-10">
      <div className="mx-auto w-full max-w-[560px] rounded-2xl border border-[#dbe9f1] bg-white p-8 shadow-sm">
        <h1 className="text-[34px] font-extrabold text-[#0c5870]">Reset Password</h1>
        <p className="mt-2 text-[15px] font-medium text-[#6a8799]">
          Set a new password for your account.
        </p>
        <form className="mt-8 space-y-5" onSubmit={onSubmit}>
          <div>
            <label htmlFor="password" className="mb-2 block text-[13px] font-extrabold uppercase tracking-[0.12em] text-[#8ca9ba]">
              New Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="h-12 w-full rounded-[12px] border border-[#c8dbe7] px-4 text-[15px]"
              placeholder="Minimum 8 characters"
            />
          </div>
          <div>
            <label htmlFor="confirm-password" className="mb-2 block text-[13px] font-extrabold uppercase tracking-[0.12em] text-[#8ca9ba]">
              Confirm Password
            </label>
            <input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              className="h-12 w-full rounded-[12px] border border-[#c8dbe7] px-4 text-[15px]"
              placeholder="Re-enter password"
            />
          </div>

          {errorMessage ? <p className="rounded-md bg-red-50 px-3 py-2 text-[13px] text-red-600">{errorMessage}</p> : null}
          {successMessage ? <p className="rounded-md bg-emerald-50 px-3 py-2 text-[13px] text-emerald-700">{successMessage}</p> : null}

          <button
            type="submit"
            disabled={!canSubmit || isSubmitting}
            className="h-12 w-full rounded-full bg-[#0c8dab] text-[15px] font-extrabold text-white disabled:opacity-60"
          >
            {isSubmitting ? 'Updating...' : 'Update Password'}
          </button>
        </form>
        <div className="mt-6 text-center">
          <Link to="/login" className="text-[14px] font-extrabold text-[#1f8baa]">Back to Login</Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
